import AnnotationAPI from '../restAPI/AnnotationAPI';

/**
 * Check if a file path is an annotation URL
 * @param {string} filePath - The file path to check
 * @returns {boolean} - True if it's an annotation URL
 */
export const isAnnotationUrl = (filePath) => {
    return filePath && filePath.startsWith('http');
};

/**
 * Check if annotation system is enabled and file is annotatable
 * @param {string} filePath - The file path to check
 * @returns {boolean} - True if annotation is available
 */
export const isAnnotationAvailable = (filePath) => {
    return AnnotationAPI.isEnabled() && isAnnotationUrl(filePath);
};

/**
 * Get the appropriate view component for a document
 * @param {Object} attachment - The attachment object
 * @param {string} attachment.filePath - The file path
 * @param {boolean} attachment.isPdf - Whether it's a PDF file
 * @returns {string} - 'annotation', 'local', or 'download'
 */
export const getDocumentViewType = (attachment) => {
    if (!attachment) return 'download';
    
    // Check if annotation is available
    if (isAnnotationAvailable(attachment.filePath)) {
        return 'annotation';
    }
    
    // Check if it's a PDF that could be annotated
    if (attachment.isPdf && AnnotationAPI.isEnabled()) {
        return 'local'; // Local PDF that could be uploaded to annotation system
    }
    
    // Fallback to download
    return 'download';
};

/**
 * Create annotation URL with authentication parameters
 * @param {string} annotationUrl - The base annotation URL
 * @param {string} token - Authentication token
 * @param {string} companyId - Company ID
 * @returns {string} - The complete annotation URL with auth
 */
export const createAnnotationUrlWithAuth = (annotationUrl, token, companyId) => {
    if (!annotationUrl) return null;
    
    try {
        const url = new URL(annotationUrl);
        url.searchParams.set('token', token);
        url.searchParams.set('companyId', companyId);
        return url.toString();
    } catch (error) {
        console.error('Error creating annotation URL with auth:', error);
        return annotationUrl;
    }
};

/**
 * Get annotation status indicator props
 * @param {Object} attachment - The attachment object
 * @returns {Object|null} - Tag props or null
 */
export const getAnnotationStatusProps = (attachment) => {
    if (!isAnnotationAvailable(attachment?.filePath)) {
        return null;
    }
    
    return {
        color: 'blue',
        icon: 'EditOutlined',
        children: 'Annotatable'
    };
}; 